import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Briefcase, ArrowRight, Heart, TrendingUp, Users, GraduationCap, Coffee, Upload, MapPin, Clock } from "lucide-react";
import { Reveal, SectionTitle } from "../components/common";
import { publicApi } from "../lib/api";
import Seo from "../components/Seo";
import CvUploadModal from "../components/CvUploadModal";

const VALUES = [
  { icon: Heart, title: "More than numbers", text: "We genuinely care about our clients and each other. Being approachable, proactive and human matters as much as being technically brilliant." },
  { icon: TrendingUp, title: "Always growing", text: "We're an award-winning Xero Platinum Partner that keeps evolving. You'll be encouraged to learn, develop and grow your career with us." },
  { icon: Users, title: "A real team", text: "No silos and no egos. Everyone has a dedicated remit but we support one another to do great work for the businesses we look after." },
];

const BENEFITS = [
  { icon: GraduationCap, title: "Training & development", text: "Ongoing support with qualifications, software certifications and professional growth." },
  { icon: Coffee, title: "A supportive culture", text: "Flexible, friendly and focused on wellbeing — because good work comes from happy people." },
  { icon: TrendingUp, title: "Room to progress", text: "As we grow, so do the opportunities. We love to promote and develop from within." },
];

export default function Careers() {
  const [cvOpen, setCvOpen] = useState(false);
  const [activeVacancy, setActiveVacancy] = useState(null);
  const [vacancies, setVacancies] = useState([]);

  useEffect(() => { publicApi.vacancies().then(setVacancies).catch(() => setVacancies([])); }, []);

  const openApply = (vacancy = null) => { setActiveVacancy(vacancy); setCvOpen(true); };
  const closeApply = () => { setCvOpen(false); setActiveVacancy(null); };

  return (
    <div data-testid="careers-page">
      <CvUploadModal open={cvOpen} onClose={closeApply} vacancy={activeVacancy} />
      <Seo
        title="Careers at CASS"
        description="Join Cloud Accounting Support Services — an award-winning, growing Xero Platinum Partner in Tamworth. Explore life at CASS, our culture and how to apply."
      />

      {/* Hero */}
      <section className="numbers-pattern text-white">
        <div className="relative max-w-4xl mx-auto px-6 py-20 md:py-24 text-center">
          <span className="inline-flex items-center gap-2 text-orange font-bold text-sm tracking-widest uppercase"><Briefcase size={16} /> Careers</span>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl uppercase leading-[0.95] mt-3">Build Your Career<br />With CASS</h1>
          <p className="mt-6 text-slate-300 leading-relaxed max-w-2xl mx-auto">
            We're a growing, award-winning finance team on a mission to give business owners far more than numbers. If you're brilliant with people as well as figures, we'd love to hear from you.
          </p>
          <button onClick={() => openApply(null)} data-testid="careers-hero-cta" className="mt-8 inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5">
            Send us your CV <Upload size={17} />
          </button>
        </div>
      </section>

      {/* Current vacancies */}
      {vacancies.length > 0 && (
        <section className="py-16 md:py-24 bg-white" data-testid="careers-vacancies">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <Reveal><SectionTitle eyebrow="Open roles" title="Current vacancies" subtitle="We're hiring! Explore our open roles below and apply in a couple of minutes." center /></Reveal>
            <div className="grid md:grid-cols-2 gap-6 mt-12">
              {vacancies.map((v, i) => (
                <Reveal key={v.id} delay={i * 90}>
                  <div className="h-full bg-slate-50 rounded-3xl border border-slate-100 p-8 flex flex-col" data-testid={`vacancy-${v.id}`}>
                    <h3 className="font-display font-bold text-navy text-xl mb-3">{v.title}</h3>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {v.location && <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy bg-navy/5 px-3 py-1 rounded-full"><MapPin size={12} /> {v.location}</span>}
                      {v.type && <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-orange bg-orange/10 px-3 py-1 rounded-full"><Clock size={12} /> {v.type}</span>}
                    </div>
                    {v.description && <p className="text-slate-500 text-sm leading-relaxed mb-6 whitespace-pre-wrap flex-1">{v.description}</p>}
                    <button onClick={() => openApply(v)} data-testid={`vacancy-apply-${v.id}`} className="self-start inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-white font-bold text-sm px-6 py-3 rounded-full transition-all hover:-translate-y-0.5">
                      Apply for this role <ArrowRight size={15} />
                    </button>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Values */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal><SectionTitle eyebrow="Life at CASS" title="What it's like to work here" subtitle="We've built the kind of practice we'd want to work in — modern, proactive and genuinely supportive." center /></Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.title} delay={i * 100}>
                  <div className="h-full bg-white rounded-3xl border border-slate-100 p-8">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-orange/10 text-orange mb-5"><Icon size={22} /></span>
                    <h3 className="font-display font-bold text-navy text-lg mb-2">{v.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed">{v.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal><SectionTitle eyebrow="Why join us" title="More than a job" center /></Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={b.title} delay={i * 100}>
                  <div className="h-full bg-slate-50 rounded-3xl border border-slate-100 p-8">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-navy/5 text-navy mb-5"><Icon size={22} /></span>
                    <h3 className="font-display font-bold text-navy text-lg mb-2">{b.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed">{b.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Apply CTA */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="relative overflow-hidden rounded-3xl bg-navy text-white p-8 md:p-14 text-center" data-testid="careers-apply">
            <h2 className="font-display font-extrabold text-3xl md:text-4xl leading-tight">{vacancies.length > 0 ? "Not the right role? Get in touch anyway." : "No current vacancy? Get in touch anyway."}</h2>
            <p className="mt-5 text-slate-300 leading-relaxed max-w-2xl mx-auto">
              We're always keen to hear from talented, people-focused finance professionals. Send us your CV and a note about what you're looking for, and we'll be in touch if there's a fit — now or in the future.
            </p>
            <div className="mt-8 flex flex-wrap gap-4 justify-center">
              <button onClick={() => openApply(null)} data-testid="careers-apply-cv" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5">
                <Upload size={17} /> Upload your CV
              </button>
              <Link to="/contact" data-testid="careers-contact" className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-bold px-8 py-4 rounded-full transition-all">
                Contact us <ArrowRight size={17} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
